import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { useAuth } from '../contexts/AuthContext';
import AppLayout from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { X, Package, Search, Edit, Save, Trash2, Brain, Sparkles, AlertTriangle, CheckCircle, Download, Ruler } from 'lucide-react';
import RichTextEditor from '../components/RichTextEditor';
import FabricCareSelector from '../components/FabricCareSelector';
import SizeTableGenerator from '../components/SizeTableGenerator';
import AIAnalysisModal from '../components/AIAnalysisModal';
import { useSearchParams } from 'react-router-dom';

interface AIConsistencyCheck {
  isConsistent: boolean;
  inconsistencyScore: number;
  reason: string;  // Tutarsızlık sebebi
  checkedAt?: Date;
  modelUsed?: string;
}

interface TicimaxProduct {
  _id: string;
  urunKartId: number;
  urunAdi: string;
  onYazi: string;
  aciklama: string;
  manuelOnYazi: string;
  manuelAciklama: string;
  lastSyncDate: Date;
  aiConsistencyCheck?: AIConsistencyCheck;
  controlled?: boolean;  // Kontrol edildi durumu
}

type FilterType = 'all' | 'eksik-onyazi' | 'eksik-aciklama' | 'ai-tutarsiz' | 'ai-taranmamis';

const TicimaxContentPanel: React.FC = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [filteredProducts, setFilteredProducts] = useState<TicimaxProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState<TicimaxProduct | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [productToDelete, setProductToDelete] = useState<TicimaxProduct | null>(null);
  const [search, setSearch] = useState('');
  
  // URL'den gelen filter parametresini al
  const initialFilter = searchParams.get('filter') as FilterType || 'eksik-onyazi';
  const [filter, setFilter] = useState<FilterType>(initialFilter);
  
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ total: 0, eksikOnYazi: 0, eksikAciklama: 0 });
  const limit = 50;
  
  // AI Stats
  const [aiStats, setAiStats] = useState({ aiTutarsiz: 0, aiTaranmamis: 0 });
  
  // AI Analysis Modal State
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiAnalysisResult, setAiAnalysisResult] = useState<any>(null);
  const [aiAnalysisLoading, setAiAnalysisLoading] = useState(false);
  const [aiAnalysisError, setAiAnalysisError] = useState<string | null>(null);
  const [analyzingProductId, setAnalyzingProductId] = useState<number | null>(null);

  const [formData, setFormData] = useState({
    manuelOnYazi: '',
    manuelAciklama: '',
    careHtml: '',
  });
  
  const [sizeTableData, setSizeTableData] = useState<{
    productType: string;
    measurements: Record<string, Record<string, string>>;
    htmlCode: string;
  } | null>(null);
  const [showSizeTable, setShowSizeTable] = useState(false);

  // HTML'i düz metne çeviren fonksiyon
  const stripHtml = (html: string): string => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const loadStats = async () => {
    try {
      const response = await axios.get('/ticimax/stats');
      
      const statsData = {
        total: response.data.totalProducts || 0,
        eksikOnYazi: response.data.eksikOnYazi || 0,
        eksikAciklama: response.data.eksikAciklama || 0,
      };
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Ticimax stats loaded:', statsData);
      }
      setStats(statsData);
      
      // AI Stats yükle
      try {
        const aiResponse = await axios.get('/openai/stats');
        setAiStats({
          aiTutarsiz: aiResponse.data.inconsistentProducts || 0,
          aiTaranmamis: (aiResponse.data.totalProducts || 0) - (aiResponse.data.scannedProducts || 0),
        });
      } catch (aiError) {
        console.warn('AI stats yüklenemedi:', aiError);
      }
    } catch (error) {
      console.error('❌ Stats yüklenemedi:', error);
    }
  };

  const loadProducts = async () => {
    try {
      setLoading(true);
      
      // AI filtreleri için ayrı endpoint kullan
      if (filter === 'ai-tutarsiz') {
        const params: any = { page, limit, minScore: 0 };
        if (search) {
          params.search = search; // Arama desteği
        }
        const response = await axios.get('/openai/inconsistent-products', { params });
        setFilteredProducts(response.data.data || []);
        setTotal(response.data.total || 0);
        return;
      }
      
      const params: any = { page, limit };
      
      if (search) {
        params.search = search;
      }
      
      if (filter === 'eksik-onyazi') {
        params.eksikOnYazi = 'true';
      } else if (filter === 'eksik-aciklama') {
        params.eksikAciklama = 'true';
      } else if (filter === 'ai-taranmamis') {
        params.aiTaranmamis = 'true';
      }

      const response = await axios.get('/ticimax/products', { params });
      
      setFilteredProducts(response.data.data);
      setTotal(response.data.total);
      
    } catch (error) {
      toast.error('Ürünler yüklenemedi!');
      console.error('Load products error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filter, search]);

  useEffect(() => {
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEdit = (product: TicimaxProduct) => {
    setSelectedProduct(product);
    
    // Eğer manuel girilmiş varsa onu kullan, yoksa Ticimax'tan gelen HTML'i düz metne çevir
    const onYaziValue = product.manuelOnYazi || (product.onYazi ? stripHtml(product.onYazi) : '');
    const aciklamaValue = product.manuelAciklama || (product.aciklama ? stripHtml(product.aciklama) : '');
    
    setFormData({
      manuelOnYazi: onYaziValue,
      manuelAciklama: aciklamaValue,
      careHtml: '',
    });
    setSizeTableData(null);
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProduct) return;

    // Edge case: Boş alan kontrolü
    if (!formData.manuelOnYazi.trim()) {
      toast.error('Önyazı boş olamaz!');
      return;
    }

    if (!formData.manuelAciklama.trim()) {
      toast.error('Açıklama boş olamaz!');
      return;
    }

    const sizeTableHtml = sizeTableData?.htmlCode || '';
    const finalAciklama = formData.manuelAciklama 
      + (formData.careHtml ? '\n\n' + formData.careHtml : '')
      + (sizeTableHtml ? '\n\n' + sizeTableHtml : '');

    // Edge case: Boyut kontrolleri (backend'le aynı limitler)
    const MAX_ONYAZI_LENGTH = 50000; // 50KB
    const MAX_ACIKLAMA_LENGTH = 500000; // 500KB

    if (formData.manuelOnYazi.length > MAX_ONYAZI_LENGTH) {
      toast.error(`Önyazı çok uzun! Maksimum ${MAX_ONYAZI_LENGTH.toLocaleString()} karakter olabilir. (Şu an: ${formData.manuelOnYazi.length.toLocaleString()})`);
      return;
    }

    if (finalAciklama.length > MAX_ACIKLAMA_LENGTH) {
      toast.error(`Açıklama çok uzun! Maksimum ${MAX_ACIKLAMA_LENGTH.toLocaleString()} karakter olabilir. (Şu an: ${finalAciklama.length.toLocaleString()})`);
      return;
    }

    // Edge case: UrunKartId validasyonu
    if (!selectedProduct.urunKartId || selectedProduct.urunKartId <= 0) {
      toast.error('Geçersiz ürün ID!');
      return;
    }

    try {
      toast.loading('Ticimax\'a gönderiliyor...', { id: 'update-content' });

      if (process.env.NODE_ENV === 'development') {
        console.log('Ticimax manual content payload summary:', {
          urunKartId: selectedProduct.urunKartId,
          onYaziLength: formData.manuelOnYazi.length,
          aciklamaLength: finalAciklama.length,
          hasCareHtml: !!formData.careHtml,
          sizeKb: ((formData.manuelOnYazi.length + finalAciklama.length) / 1024).toFixed(2)
        });
      }

      // Request body olarak gönder (URL params değil) - 431 hatasını önler
      const response = await axios.post('/ticimax/update-manual-content', {
        urunKartId: selectedProduct.urunKartId,
        manuelOnYazi: formData.manuelOnYazi,
        manuelAciklama: finalAciklama,
      });

      if (process.env.NODE_ENV === 'development') {
        console.log('Ticimax manual content updated:', response.status);
      }

      toast.success('✅ İçerik başarıyla güncellendi ve Ticimax\'a gönderildi!', { id: 'update-content' });
      
      setShowModal(false);
      setSelectedProduct(null);
      loadProducts();
      loadStats(); // Stats'leri güncelle

    } catch (error: any) {
      console.error('❌ Update error:', error);
      
      // Edge case: Farklı hata türleri için özel mesajlar
      let errorMessage = 'Güncelleme hatası!';
      
      if (error.response) {
        // Backend'den gelen hata mesajı
        errorMessage = error.response.data?.message || error.response.data?.error || error.message;
        
        // HTTP status kodlarına göre özel mesajlar
        switch (error.response.status) {
          case 400:
            errorMessage = `Geçersiz veri: ${errorMessage}`;
            break;
          case 404:
            errorMessage = 'Ürün bulunamadı. Lütfen önce senkronizasyon yapın.';
            break;
          case 413:
            errorMessage = 'Gönderilen veri çok büyük! Lütfen açıklamayı kısaltın.';
            break;
          case 431:
            errorMessage = 'İstek başlıkları çok büyük! Lütfen açıklamayı kısaltın.';
            break;
          case 503:
            errorMessage = 'Ticimax\'a bağlanılamadı. Lütfen daha sonra tekrar deneyin.';
            break;
          case 500:
            errorMessage = 'Sunucu hatası. Lütfen daha sonra tekrar deneyin.';
            break;
        }
      } else if (error.request) {
        errorMessage = 'Sunucuya ulaşılamadı. İnternet bağlantınızı kontrol edin.';
      }
      
      toast.error(errorMessage, { id: 'update-content', duration: 5000 });
    }
  };

  // Silme işlemleri (sadece admin)
  const handleDeleteClick = (product: TicimaxProduct) => {
    // Edge case: User null/undefined kontrolü
    if (!user) {
      toast.error('Oturum bilgisi bulunamadı. Lütfen tekrar giriş yapın.');
      return;
    }

    // Edge case: Yetki kontrolü
    if (user.role !== 'admin') {
      toast.error('Bu işlem için admin yetkisi gerekiyor!');
      return;
    }

    // Edge case: Product null/undefined kontrolü
    if (!product || !product.urunKartId) {
      toast.error('Geçersiz ürün bilgisi!');
      return;
    }

    setProductToDelete(product);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    // Edge case: Null check
    if (!productToDelete) {
      toast.error('Silinecek ürün bulunamadı!');
      return;
    }

    // Edge case: UrunKartId validasyonu
    if (!productToDelete.urunKartId || productToDelete.urunKartId <= 0) {
      toast.error('Geçersiz ürün ID!');
      setShowDeleteDialog(false);
      setProductToDelete(null);
      return;
    }

    try {
      toast.loading('Ürün siliniyor...', { id: 'delete-product' });

      await axios.delete(`/ticimax/products/${productToDelete.urunKartId}`, {
        timeout: 10000 // 10 saniye timeout
      });

      toast.success(`✅ Ürün başarıyla silindi: ${productToDelete.urunAdi || 'Ürün'}`, { id: 'delete-product' });

      // State'i güncelle
      setShowDeleteDialog(false);
      setProductToDelete(null);
      
      // Edge case: Promise.all ile paralel güncelleme
      try {
        await Promise.all([loadProducts(), loadStats()]);
      } catch (refreshError) {
        console.warn('Liste yenilenirken hata:', refreshError);
        // Silme başarılı oldu ama refresh başarısız - kullanıcıya bildir
        toast('⚠️ Liste yenilenirken hata oluştu. Sayfayı yenileyin.', { duration: 3000 });
      }

    } catch (error: any) {
      console.error('❌ Delete error:', error);

      let errorMessage = 'Ürün silinemedi!';

      if (error.response) {
        // Backend'den gelen hata mesajı
        errorMessage = error.response.data?.message || error.message;

        // HTTP status kodlarına göre özel mesajlar
        switch (error.response.status) {
          case 400:
            errorMessage = 'Geçersiz ürün bilgisi!';
            break;
          case 403:
            errorMessage = 'Bu işlem için yetkiniz yok!';
            break;
          case 404:
            errorMessage = 'Ürün bulunamadı! Zaten silinmiş olabilir.';
            break;
          case 409:
            errorMessage = 'Ürün başka biri tarafından zaten silinmiş!';
            break;
          case 410:
            errorMessage = 'Bu ürün zaten daha önce silinmiş!';
            break;
          case 500:
            errorMessage = 'Sunucu hatası. Lütfen daha sonra tekrar deneyin.';
            break;
          case 503:
            errorMessage = 'Veritabanına bağlanılamadı. Lütfen daha sonra tekrar deneyin.';
            break;
        }
      } else if (error.request) {
        // Network hatası
        errorMessage = 'Sunucuya ulaşılamadı. İnternet bağlantınızı kontrol edin.';
      } else if (error.code === 'ECONNABORTED') {
        // Timeout hatası
        errorMessage = 'İstek zaman aşımına uğradı. Lütfen tekrar deneyin.';
      }

      toast.error(errorMessage, { id: 'delete-product', duration: 5000 });
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteDialog(false);
    setProductToDelete(null);
  };

  // AI Analiz fonksiyonu
  const handleAIAnalysis = async (product: TicimaxProduct) => {
    if (!product.urunKartId || product.urunKartId <= 0) {
      toast.error('Geçersiz ürün ID!');
      return;
    }

    setAnalyzingProductId(product.urunKartId);
    setAiAnalysisLoading(true);
    setAiAnalysisError(null);
    setAiAnalysisResult(null);
    setShowAIModal(true);

    try {
      const response = await axios.post(`/openai/check-product/${product.urunKartId}`);
      
      if (response.data.status === 'success') {
        setAiAnalysisResult({
          urunKartId: product.urunKartId,
          urunAdi: product.urunAdi,
          consistent: response.data.data.consistent,
          inconsistencyScore: response.data.data.inconsistencyScore,
          reason: response.data.data.reason || '',
          checkedAt: new Date().toISOString(),
        });
        
        // Listeyi güncelle
        loadProducts();
        loadStats();
      }
    } catch (error: any) {
      console.error('AI Analysis error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'AI analizi başarısız!';
      setAiAnalysisError(errorMessage);
    } finally {
      setAiAnalysisLoading(false);
      setAnalyzingProductId(null);
    }
  };

  // AI Skor badge rengini belirle
  const getAIScoreBadge = (score: number | undefined) => {
    if (score === undefined || score === null) {
      return { variant: 'outline' as const, label: 'Taranmadı', className: 'text-gray-500 bg-gray-100' };
    }
    if (score <= 30) {
      return { variant: 'default' as const, label: `${score}%`, className: 'bg-green-100 text-green-700 border-green-300' };
    }
    if (score <= 60) {
      return { variant: 'default' as const, label: `${score}%`, className: 'bg-yellow-100 text-yellow-700 border-yellow-300' };
    }
    return { variant: 'destructive' as const, label: `${score}%`, className: 'bg-red-100 text-red-700 border-red-300' };
  };

  // HTML'i düz metne çeviren fonksiyon (export için)
  // Excel hücre limiti 32767 karakter - güvenlik için 30000'de kesiyoruz
  const stripHtmlForExport = (html: string, maxLength: number = 30000): string => {
    if (!html) return '';
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    const text = (tmp.textContent || tmp.innerText || '').trim();
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + '... (kırpıldı)';
    }
    return text;
  };

  // Excel Export fonksiyonu - TÜM ürünleri indirir (pagination'dan bağımsız)
  const [exporting, setExporting] = useState(false);
  
  const handleExportExcel = async () => {
    if (total === 0) {
      toast.error('Dışa aktarılacak ürün bulunamadı!');
      return;
    }

    setExporting(true);
    toast.loading(`${total} ürün indiriliyor...`, { id: 'excel-export' });

    try {
      // TÜM ürünleri çek (pagination'sız)
      let allProducts: TicimaxProduct[] = [];
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Excel export başlıyor, filter:', filter);
      }
      
      if (filter === 'ai-tutarsiz') {
        // AI tutarsız için ayrı endpoint
        const response = await axios.get('/openai/inconsistent-products', { 
          params: { page: 1, limit: 10000, minScore: 0, search: search || undefined } 
        });
        if (process.env.NODE_ENV === 'development') {
          console.log('AI export response count:', response.data?.data?.length || 0);
        }
        allProducts = response.data?.data || [];
      } else {
        // Diğer filtreler için
        const params: any = { page: 1, limit: 10000 };
        if (search) params.search = search;
        if (filter === 'eksik-onyazi') params.eksikOnYazi = 'true';
        if (filter === 'eksik-aciklama') params.eksikAciklama = 'true';
        if (filter === 'ai-taranmamis') params.aiTaranmamis = 'true';
        
        const response = await axios.get('/ticimax/products', { params });
        if (process.env.NODE_ENV === 'development') {
          console.log('Ticimax export response count:', response.data?.data?.length || 0);
        }
        allProducts = response.data?.data || [];
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('Çekilen ürün sayısı:', allProducts.length);
      }

      if (allProducts.length === 0) {
        toast.error('Dışa aktarılacak ürün bulunamadı!', { id: 'excel-export' });
        setExporting(false);
        return;
      }

      // Excel için veri hazırla (hücre limiti 32767 karakter)
      const exportData = allProducts.map(product => ({
        'Kontrol Edildi': product.controlled ? 'Evet' : 'Hayır',
        'Ticimax ID': product.urunKartId,
        'Ürün Adı': (product.urunAdi || '').substring(0, 500),
        'AI Skor (%)': product.aiConsistencyCheck?.inconsistencyScore ?? '-',
        'Tutarsızlık Sebebi': (product.aiConsistencyCheck?.reason || '-').substring(0, 1000),
        'Tutarlı mı?': product.aiConsistencyCheck?.isConsistent ? 'Evet' : 'Hayır',
        'Önyazı (Ticimax)': stripHtmlForExport(product.onYazi, 15000),
        'Açıklama (Ticimax)': stripHtmlForExport(product.aciklama, 15000),
        'Manuel Önyazı': stripHtmlForExport(product.manuelOnYazi, 15000),
        'Manuel Açıklama': stripHtmlForExport(product.manuelAciklama, 15000),
        'Son Sync': product.lastSyncDate ? new Date(product.lastSyncDate).toLocaleString('tr-TR') : '-',
        'AI Kontrol Tarihi': product.aiConsistencyCheck?.checkedAt 
          ? new Date(product.aiConsistencyCheck.checkedAt).toLocaleString('tr-TR') 
          : '-',
        'AI Model': product.aiConsistencyCheck?.modelUsed || '-',
      }));

      // Worksheet oluştur
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      // Sütun genişliklerini ayarla
      const columnWidths = [
        { wch: 14 },  // Kontrol Edildi
        { wch: 12 },  // Ticimax ID
        { wch: 40 },  // Ürün Adı
        { wch: 12 },  // AI Skor
        { wch: 50 },  // Tutarsızlık Sebebi
        { wch: 12 },  // Tutarlı mı?
        { wch: 50 },  // Önyazı
        { wch: 60 },  // Açıklama
        { wch: 50 },  // Manuel Önyazı
        { wch: 60 },  // Manuel Açıklama
        { wch: 20 },  // Son Sync
        { wch: 20 },  // AI Kontrol Tarihi
        { wch: 15 },  // AI Model
      ];
      worksheet['!cols'] = columnWidths;

      // Workbook oluştur
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Ürün Raporu');

      // Dosya adı oluştur
      const filterName = filter === 'ai-tutarsiz' ? 'AI_Tutarsiz' : 
                         filter === 'ai-taranmamis' ? 'AI_Taranmamis' :
                         filter === 'eksik-onyazi' ? 'Eksik_Onyazi' :
                         filter === 'eksik-aciklama' ? 'Eksik_Aciklama' : 'Tum_Urunler';
      const dateStr = new Date().toISOString().split('T')[0];
      const fileName = `Ticimax_${filterName}_${dateStr}.xlsx`;

      // Excel dosyasını indir
      XLSX.writeFile(workbook, fileName);
      
      toast.success(`${allProducts.length} ürün Excel'e aktarıldı!`, { id: 'excel-export' });
    } catch (error: any) {
      console.error('Excel export error:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Bilinmeyen hata';
      toast.error(`Excel hatası: ${errorMessage}`, { id: 'excel-export' });
    } finally {
      setExporting(false);
    }
  };

  // Controlled checkbox değişikliği
  // Edge case: Optimistic update ile hata durumunda geri alma
  const handleControlledChange = async (product: TicimaxProduct, checked: boolean) => {
    // Edge case: product null kontrolü
    if (!product || !product.urunKartId) {
      toast.error('Geçersiz ürün bilgisi!');
      return;
    }

    const previousValue = product.controlled;

    // Optimistic update - UI'ı hemen güncelle
    setFilteredProducts(prev => 
      prev.map(p => 
        p.urunKartId === product.urunKartId 
          ? { ...p, controlled: checked }
          : p
      )
    );

    try {
      await axios.post('/ticimax/update-controlled', {
        urunKartId: product.urunKartId,
        controlled: checked,
      });

      toast.success(checked ? 'Kontrol edildi olarak işaretlendi' : 'Kontrol edilmedi olarak işaretlendi');
    } catch (error: any) {
      console.error('Controlled update error:', error);
      
      // Edge case: Hata durumunda state'i geri al
      setFilteredProducts(prev => 
        prev.map(p => 
          p.urunKartId === product.urunKartId 
            ? { ...p, controlled: previousValue }
            : p
        )
      );

      const errorMessage = error.response?.data?.message || 'Güncelleme başarısız!';
      toast.error(errorMessage);
    }
  };

  // Admin kontrolü
  const isAdmin = user?.role === 'admin';

  return (
    <AppLayout title="Ticimax İçerik Yönetimi" subtitle="Manuel Ürün Açıklama Girişi - Content Departmanı">
      
      <div className="container mx-auto px-4 py-8 space-y-6">
        
        {/* Stats */}
        <div className="grid grid-cols-3 md:grid-cols-5 gap-4">
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filter === 'all' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setFilter('all')}
          >
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Tüm Ürünler</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats.total}</div>
            </CardContent>
          </Card>

          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filter === 'eksik-onyazi' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setFilter('eksik-onyazi')}
          >
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Eksik Önyazı</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-red-600">{stats.eksikOnYazi}</div>
            </CardContent>
          </Card>

          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filter === 'eksik-aciklama' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setFilter('eksik-aciklama')}
          >
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Eksik Açıklama</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-orange-600">{stats.eksikAciklama}</div>
            </CardContent>
          </Card>

          {/* AI Tutarsız Kartı */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg border-purple-200 ${filter === 'ai-tutarsiz' ? 'ring-2 ring-purple-500' : ''}`}
            onClick={() => setFilter('ai-tutarsiz')}
          >
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-1">
                <AlertTriangle className="h-4 w-4 text-purple-600" />
                AI Tutarsız
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-purple-600">{aiStats.aiTutarsiz}</div>
            </CardContent>
          </Card>

          {/* AI Taranmamış Kartı */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg border-gray-200 ${filter === 'ai-taranmamis' ? 'ring-2 ring-gray-500' : ''}`}
            onClick={() => setFilter('ai-taranmamis')}
          >
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-1">
                <Brain className="h-4 w-4 text-gray-500" />
                Taranmadı
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-500">{aiStats.aiTaranmamis}</div>
            </CardContent>
          </Card>
        </div>

        {/* Search & Export & Admin Actions */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-3">
              <div className="flex-1 min-w-[200px] relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Ürün adı ile ara..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
              {/* Excel Export Butonu - Tüm ürünleri indirir */}
              <Button
                variant="outline"
                onClick={handleExportExcel}
                disabled={total === 0 || loading || exporting}
                className="border-green-300 text-green-700 hover:bg-green-50"
              >
                {exporting ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mr-2"></div>
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Excel İndir ({total})
              </Button>
              
              {/* Admin AI Tarama Butonları */}
              {isAdmin && (
                <>
                  <Button
                    variant="outline"
                    onClick={async () => {
                      try {
                        toast.loading('Yeni ürünler taranıyor...', { id: 'scan-new' });
                        const response = await axios.post('/openai/scan-all', { forceRescan: false });
                        toast.success(`Tarama tamamlandı! Tutarsız: ${response.data.result?.inconsistent || 0}`, { id: 'scan-new' });
                        loadProducts();
                        loadStats();
                      } catch (error: any) {
                        toast.error(error.response?.data?.message || 'Tarama başarısız!', { id: 'scan-new' });
                      }
                    }}
                    className="border-purple-300 text-purple-700 hover:bg-purple-50"
                  >
                    <Brain className="w-4 h-4 mr-2" />
                    Yenileri Tara
                  </Button>
                  <Button
                    variant="outline"
                    onClick={async () => {
                      try {
                        toast.loading('Kontrol edilmemişler taranıyor...', { id: 'scan-uncontrolled' });
                        const response = await axios.post('/openai/scan-uncontrolled', {});
                        toast.success(`Tarama tamamlandı! Tutarsız: ${response.data.result?.inconsistent || 0}`, { id: 'scan-uncontrolled' });
                        loadProducts();
                        loadStats();
                      } catch (error: any) {
                        toast.error(error.response?.data?.message || 'Tarama başarısız!', { id: 'scan-uncontrolled' });
                      }
                    }}
                    className="border-orange-300 text-orange-700 hover:bg-orange-50"
                  >
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Kontrol Edilmemişleri Tara
                  </Button>
                  <Button
                    variant="outline"
                    onClick={async () => {
                      if (!window.confirm('Tüm ürünler yeniden taranacak. Bu işlem uzun sürebilir. Devam etmek istiyor musunuz?')) return;
                      try {
                        toast.loading('Tüm ürünler taranıyor...', { id: 'scan-all' });
                        const response = await axios.post('/openai/scan-all', { forceRescan: true });
                        toast.success(`Tarama tamamlandı! Tutarsız: ${response.data.result?.inconsistent || 0}`, { id: 'scan-all' });
                        loadProducts();
                        loadStats();
                      } catch (error: any) {
                        toast.error(error.response?.data?.message || 'Tarama başarısız!', { id: 'scan-all' });
                      }
                    }}
                    className="border-red-300 text-red-700 hover:bg-red-50"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Tümünü Yeniden Tara
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Product List */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">Ürün bulunamadı</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-center py-4 px-2 font-semibold text-sm">
                        <div className="flex items-center justify-center gap-1">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        </div>
                      </th>
                      <th className="text-left py-4 px-2 font-semibold text-sm">Ticimax ID</th>
                      <th className="text-left py-4 px-2 font-semibold text-sm">Ürün Adı</th>
                      <th className="text-center py-4 px-2 font-semibold text-sm">
                        <div className="flex items-center justify-center gap-1">
                          <Brain className="h-4 w-4 text-purple-600" />
                          AI Skor
                        </div>
                      </th>
                      <th className="text-left py-4 px-2 font-semibold text-sm">Sebep</th>
                      <th className="text-left py-4 px-2 font-semibold text-sm">Son Sync</th>
                      <th className="text-center py-4 px-2 font-semibold text-sm">İşlem</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredProducts.map(product => {
                      const aiScore = product.aiConsistencyCheck?.inconsistencyScore;
                      const scoreBadge = getAIScoreBadge(aiScore);
                      
                      return (
                        <tr key={product._id} className="hover:bg-gray-50">
                          <td className="py-4 px-2 text-center">
                            <input
                              type="checkbox"
                              checked={product.controlled || false}
                              onChange={(e) => handleControlledChange(product, e.target.checked)}
                              className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer"
                              title={product.controlled ? 'Kontrol edildi' : 'Kontrol edilmedi'}
                            />
                          </td>
                          <td className="py-4 px-2">
                            <Badge variant="outline">{product.urunKartId}</Badge>
                          </td>
                          <td className="py-4 px-2">
                            <div className="font-medium text-sm">{product.urunAdi}</div>
                          </td>
                          <td className="py-4 px-2 text-center">
                            <div className="flex items-center justify-center gap-1">
                              {aiScore !== undefined ? (
                                <Badge 
                                  variant="outline" 
                                  className={`text-xs font-semibold ${scoreBadge.className}`}
                                >
                                  {aiScore <= 30 && <CheckCircle className="h-3 w-3 mr-1" />}
                                  {aiScore > 60 && <AlertTriangle className="h-3 w-3 mr-1" />}
                                  {scoreBadge.label}
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs text-gray-400">
                                  -
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="py-4 px-2 text-sm text-muted-foreground max-w-xs">
                            {product.aiConsistencyCheck?.reason ? (
                              <span 
                                className="line-clamp-2" 
                                title={product.aiConsistencyCheck.reason}
                              >
                                {product.aiConsistencyCheck.reason}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="py-4 px-2 text-sm text-muted-foreground">
                            {product.lastSyncDate ? new Date(product.lastSyncDate).toLocaleString('tr-TR') : '-'}
                          </td>
                          <td className="py-4 px-2 text-center">
                            <div className="flex items-center justify-center gap-2">
                              {/* AI Analiz Butonu */}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleAIAnalysis(product)}
                                disabled={analyzingProductId === product.urunKartId}
                                className="border-purple-300 text-purple-700 hover:bg-purple-50"
                                title="AI Tutarsızlık Analizi"
                              >
                                {analyzingProductId === product.urunKartId ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 mr-1"></div>
                                ) : (
                                  <Sparkles className="w-4 h-4 mr-1" />
                                )}
                                AI
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleEdit(product)}
                              >
                                <Edit className="w-4 h-4 mr-2" />
                                İçerik Gir
                              </Button>
                              {isAdmin && (
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleDeleteClick(product)}
                                  title="Ürünü Sil (ADMIN)"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {!loading && total > limit && (
              <div className="flex items-center justify-between p-4 border-t">
                <div className="text-sm text-muted-foreground">
                  Toplam {total} ürün - Sayfa {page} / {Math.ceil(total / limit)}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                  >
                    Önceki
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= Math.ceil(total / limit)}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Sonraki
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Manuel Açıklama Modal */}
      {showModal && selectedProduct && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 border-b">
              <div>
                <CardTitle className="text-lg">Manuel İçerik Girişi</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {selectedProduct.urunAdi}
                </p>
                <Badge variant="outline" className="mt-2">
                  Ticimax ID: {selectedProduct.urunKartId}
                </Badge>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowModal(false);
                  setSelectedProduct(null);
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>

            <CardContent className="flex-1 overflow-y-auto p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                
                {/* Önyazı */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium">
                    Önyazı *
                  </label>
                  <RichTextEditor
                    value={formData.manuelOnYazi}
                    onChange={(value) => setFormData({ ...formData, manuelOnYazi: value })}
                    placeholder="Ürünün kısa tanıtım yazısını giriniz..."
                    minHeight="120px"
                    required
                  />
                </div>

                {/* Açıklama */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium">
                    Açıklama * (Bakım sembolleri otomatik burada görünecek)
                  </label>
                  <RichTextEditor
                    value={formData.manuelAciklama + (formData.careHtml ? '\n\n' + formData.careHtml : '')}
                    onChange={(value) => {
                      // Bakım HTML'ini çıkar, sadece manuel açıklamayı kaydet
                      let cleanValue = value;
                      if (formData.careHtml) {
                        cleanValue = value.replace(formData.careHtml, '').replace(/\n\n$/, '');
                      }
                      setFormData({ ...formData, manuelAciklama: cleanValue });
                    }}
                    placeholder="Ürünün detaylı açıklamasını giriniz..."
                    minHeight="200px"
                    required
                  />
                </div>

                {/* Kumaş ve Bakım Bilgileri - Ayrı bölüm olarak */}
                <FabricCareSelector
                  value={formData.careHtml}
                  onChange={(html) => {
                    setFormData({ ...formData, careHtml: html });
                  }}
                />

                {/* Beden Tablosu */}
                <div className="space-y-3 border-t pt-4">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium">Beden Tablosu</label>
                    <Button 
                      type="button" 
                      variant={sizeTableData ? "default" : "outline"}
                      size="sm" 
                      onClick={() => setShowSizeTable(true)}
                    >
                      <Ruler className="w-4 h-4 mr-2" />
                      {sizeTableData ? (
                        <span className="flex items-center gap-1">
                          <span>Beden Tablosu Mevcut</span>
                          <span className="text-green-500">✓</span>
                        </span>
                      ) : 'Beden Tablosu Oluştur'}
                    </Button>
                  </div>
                  {sizeTableData && (
                    <div className="p-3 bg-muted/20 rounded-lg border">
                      <p className="text-sm font-medium">Ürün Tipi: {sizeTableData.productType}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Beden tablosu oluşturuldu. Düzenlemek için yukarıdaki butona tıklayın.
                      </p>
                    </div>
                  )}
                </div>

                {/* Footer Buttons */}
                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    type="submit"
                    disabled={!formData.manuelOnYazi.trim() || !formData.manuelAciklama.trim()}
                    className="flex-1"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Kaydet ve Ticimax'a Gönder
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowModal(false);
                      setSelectedProduct(null);
                    }}
                    className="flex-1"
                  >
                    İptal
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Beden Tablosu Oluşturucu Modal */}
      {showSizeTable && (
        <SizeTableGenerator
          sizes={{}}
          productName={selectedProduct?.urunAdi || 'Ürün'}
          required={false}
          existingData={sizeTableData}
          onClose={() => setShowSizeTable(false)}
          onSave={(data) => {
            setSizeTableData(data);
            setShowSizeTable(false);
            toast.success('Beden tablosu kaydedildi!');
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && productToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="border-b">
              <CardTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5" />
                Ürün Silme Onayı
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800 font-medium mb-2">
                  ⚠️ Bu işlem geri alınamaz!
                </p>
                <p className="text-sm text-red-700">
                  Aşağıdaki ürünü silmek istediğinizden emin misiniz?
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">Ürün ID:</span>
                  <Badge variant="outline">{productToDelete.urunKartId}</Badge>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-600">Ürün Adı:</span>
                  <p className="text-sm font-semibold mt-1">{productToDelete.urunAdi}</p>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={handleDeleteCancel}
                >
                  İptal
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={handleDeleteConfirm}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Evet, Sil
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* AI Analysis Modal */}
      <AIAnalysisModal
        isOpen={showAIModal}
        onClose={() => {
          setShowAIModal(false);
          setAiAnalysisResult(null);
          setAiAnalysisError(null);
        }}
        result={aiAnalysisResult}
        loading={aiAnalysisLoading}
        error={aiAnalysisError}
      />
    </AppLayout>
  );
};

export default TicimaxContentPanel;
